import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';

import { clsx } from '@freelancelabs/utils';

import { SidebarContext } from './Sidebar';

interface Props {
    /** Nom affiche (ex. « Robert DURIS ») */
    name: string;
    /** Email affiche sous le nom */
    email?: string;
    /** Initiales de l'avatar ; deduites du nom si absentes */
    initials?: string;
    /** Contenu du menu deroulant (SidebarAccountItem, hr...) */
    children?: ReactNode;
    className?: string;
}

const getInitials = (name: string) =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('');

/**
 * Bloc compte en pied de sidebar (variante portail) : avatar + identite,
 * menu deroulant ouvert vers le haut, ferme au clic exterieur et a Escape.
 */
const SidebarAccount = ({ name, email, initials, children, className }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const { isShrink } = useContext(SidebarContext);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const handlePointerDown = (event: PointerEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };
        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    return (
        <div
            ref={rootRef}
            className={clsx('sidebar-account', isOpen && 'is-open', isShrink && 'sidebar-account--shrink', className)}
        >
            <button
                type="button"
                className="sidebar-account__trigger"
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-label={name}
                onClick={() => setIsOpen((open) => !open)}
            >
                <span className="sidebar-account__avatar" aria-hidden="true">
                    {initials || getInitials(name)}
                </span>
                {!isShrink && (
                    <>
                        <span className="sidebar-account__info">
                            <b>{name}</b>
                            {email && <span>{email}</span>}
                        </span>
                        <span className="sidebar-account__chevron" aria-hidden="true" />
                    </>
                )}
            </button>
            <div className="sidebar-account__menu" role="menu" onClick={() => setIsOpen(false)}>
                {children}
            </div>
        </div>
    );
};

export default SidebarAccount;
